import { useState } from 'react';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
import type { BoundingBox } from '../../../lib/inspections';
import { getScoreColor } from './hazard-colors';
import { FillVisibleIcon, FillHiddenIcon } from './photo-viewer/icons';

interface HazardArea {
  id: string;
  /** 발견사항 번호 (1부터 시작) */
  number: number;
  /** 위험 영역 좌표 (정규화된 값 0.0~1.0) */
  boundingBox: BoundingBox;
  /** AI 가능성 점수 (1~10) */
  aiLikelihoodScore?: number | null;
  /** AI 심각도 점수 (1~10) */
  aiSeverityScore?: number | null;
  /** AI 위험도 점수 (1~10, THREE_LEVEL에서 색상 결정용) */
  aiRiskLevel?: number | null;
}

interface HazardOverlayProps {
  /** 표시할 위험 영역 목록 */
  hazards: HazardArea[];
  /** 실제 이미지 표시 너비 (px) */
  containerWidth: number;
  /** 실제 이미지 표시 높이 (px) */
  containerHeight: number;
  /** object-contain으로 인한 X 오프셋 (px) */
  offsetX?: number;
  /** object-contain으로 인한 Y 오프셋 (px) */
  offsetY?: number;
  /** 선택된 위험 영역 ID (강조 표시용) */
  selectedHazardId?: string | null;
  /** 위험 영역 클릭 핸들러 */
  onHazardClick?: (hazardId: string) => void;
  /** 위험성평가 설정 (score 기반 색상 결정용) */
  riskConfig?: RiskAssessmentConfig | null;
}

/**
 * 사진 위에 위험 영역을 표시하는 오버레이 컴포넌트
 *
 * - 정규화된 좌표 (0~1)를 실제 픽셀 좌표로 변환하여 표시
 * - 각 영역에 #번호 라벨 표시
 * - 심각도에 따른 색상 구분
 * - 우상단 버튼으로 채우기 투명도 토글
 */
export function HazardOverlay({
  hazards,
  containerWidth,
  containerHeight,
  offsetX = 0,
  offsetY = 0,
  selectedHazardId,
  onHazardClick,
  riskConfig,
}: HazardOverlayProps) {
  const [fillVisible, setFillVisible] = useState(true);

  if (hazards.length === 0 || containerWidth === 0 || containerHeight === 0) {
    return null;
  }

  // 클릭 핸들러가 있으면 pointer-events 활성화
  const isClickable = !!onHazardClick;

  return (
    <div className={`absolute inset-0 overflow-hidden ${isClickable ? '' : 'pointer-events-none'}`}>
      {/* 투명도 토글 버튼 (이미지 우상단) */}
      <button
        type="button"
        onClick={() => setFillVisible((prev) => !prev)}
        className="absolute z-20 flex items-center justify-center w-7 h-7 rounded bg-black/50 hover:bg-black/70 text-white transition-colors pointer-events-auto"
        style={{ top: offsetY + 6, left: offsetX + containerWidth - 34 }}
        title={fillVisible ? '채우기 숨기기' : '채우기 표시'}
      >
        {fillVisible ? <FillVisibleIcon /> : <FillHiddenIcon />}
      </button>

      <svg
        width={containerWidth}
        height={containerHeight}
        className="absolute"
        style={{ zIndex: 10, left: offsetX, top: offsetY }}
      >
        {hazards.map((hazard) => {
          const { x, y, width, height } = hazard.boundingBox;

          // 좌표를 0~1 범위로 클램핑
          const clampedX = Math.max(0, Math.min(1, x));
          const clampedY = Math.max(0, Math.min(1, y));
          // 너비/높이도 범위를 벗어나지 않도록 조정
          const clampedWidth = Math.max(0, Math.min(1 - clampedX, width));
          const clampedHeight = Math.max(0, Math.min(1 - clampedY, height));

          // 유효하지 않은 영역은 건너뛰기
          if (clampedWidth <= 0 || clampedHeight <= 0) {
            return null;
          }

          // 정규화된 좌표를 픽셀로 변환
          const pixelX = clampedX * containerWidth;
          const pixelY = clampedY * containerHeight;
          const pixelWidth = clampedWidth * containerWidth;
          const pixelHeight = clampedHeight * containerHeight;

          // score 기반 색상 (N5b: severity 폐기 → score+config 사용)
          const color = getScoreColor(hazard.aiLikelihoodScore, hazard.aiSeverityScore, riskConfig, hazard.aiRiskLevel);

          // 선택 상태 확인
          const isSelected = selectedHazardId === hazard.id;
          const hasSelection = selectedHazardId != null;
          // 선택된 항목이 있으면 다른 항목은 흐리게 표시
          const opacity = hasSelection && !isSelected ? 0.6 : 1;

          // 라벨 크기
          const labelWidth = 24;
          const labelHeight = 20;

          // 영역이 라벨보다 작으면 라벨을 바깥에 배치
          const isBoxTooSmall = pixelWidth < labelWidth + 4 || pixelHeight < labelHeight + 4;

          // 라벨 위치 계산
          const labelX = pixelX;
          let labelY = pixelY;

          if (isBoxTooSmall) {
            // 영역 위쪽에 라벨 배치 (위쪽 공간이 없으면 아래쪽)
            if (pixelY >= labelHeight + 2) {
              labelY = pixelY - labelHeight - 2;
            } else {
              labelY = pixelY + pixelHeight + 2;
            }
          }

          // 채우기 색상 (투명도 토글 반영)
          const fillColor = fillVisible
            ? (isSelected ? color.selectedFill : color.fill)
            : 'transparent';

          return (
            <g
              key={hazard.id}
              opacity={opacity}
              onClick={isClickable ? () => onHazardClick(hazard.id) : undefined}
              style={isClickable ? { cursor: 'pointer' } : undefined}
            >
              {/* 위험 영역 사각형 */}
              <rect
                x={pixelX}
                y={pixelY}
                width={pixelWidth}
                height={pixelHeight}
                fill={fillColor}
                stroke={color.stroke}
                strokeWidth={isSelected ? 3 : 2}
                strokeDasharray={isSelected ? undefined : '4,2'}
                rx={4}
                ry={4}
              />

              {/* 번호 라벨 배경 */}
              <rect
                x={labelX}
                y={labelY}
                width={labelWidth}
                height={labelHeight}
                fill={color.stroke}
                rx={4}
                ry={4}
              />

              {/* 번호 라벨 텍스트 */}
              <text
                x={labelX + labelWidth / 2}
                y={labelY + labelHeight / 2 + 1}
                fill="white"
                fontSize={11}
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                #{hazard.number}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default HazardOverlay;
